import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { CategorySubcategoryMapping } from '../../types/categoryMapping';
import { TransactionCategory } from '../../types/index';

interface CategoryMappingRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (mapping: Omit<CategorySubcategoryMapping, 'id' | 'createdAt' | 'updatedAt'>) => void;
  editingRule: CategorySubcategoryMapping | null;
  parentCategories?: { id: number; name: string }[];
}

const TRANSACTION_CATEGORIES: TransactionCategory[] = [
  'Materials',
  'Payroll',
  'Subcontractors',
  'Equipment',
  'Equipment Rental',
  'Utilities',
  'Rent',
  'Insurance',

  'Revenue',
  'Professional Services',
  'Office Expenses',
  'Vehicle Expenses',
  'Travel',
  'Marketing',
  'Training',
  'Bank Fees',
  'Other Income',
  'Tax',
  'Uncategorised',
];

export const CategoryMappingRuleModal: React.FC<CategoryMappingRuleModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingRule,
  parentCategories,
}) => {
  const [parentCategory, setParentCategory] = useState<TransactionCategory>('Materials');
  const [parentCategoryId, setParentCategoryId] = useState<number | undefined>(undefined);
  const [subcategoryName, setSubcategoryName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);

  const categoryList = parentCategories ?? TRANSACTION_CATEGORIES.map(name => ({ id: 0, name }));
  const isLiveMode = !!parentCategories;

  useEffect(() => {
    if (editingRule) {
      setParentCategory(editingRule.parentCategory);
      setParentCategoryId(editingRule.parentCategoryId);
      setSubcategoryName(editingRule.subcategoryName);
      setDescription(editingRule.description || '');
      setIsActive(editingRule.isActive);
    } else {
      const firstCategory = categoryList[0];
      setParentCategory((firstCategory?.name ?? 'Materials') as TransactionCategory);
      setParentCategoryId(firstCategory?.id);
      setSubcategoryName('');
      setDescription('');
      setIsActive(true);
    }
  }, [editingRule, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = () => {
    if (!subcategoryName.trim()) {
      return;
    }

    onSave({
      parentCategory,
      parentCategoryId,
      subcategoryName: subcategoryName.trim(),
      description: description.trim() || undefined,
      isActive,
    });

    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {editingRule ? 'Edit Child Category' : 'Add Child Category'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="parentCategory">Parent Category</Label>
            {isLiveMode && categoryList.length === 0 ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                No parent categories have been synced from your data sources yet. To populate this list, go to System Settings &rarr; Data Source Config, connect a source, and enable the Parent Category Source toggle.
              </div>
            ) : (
              <Select
                value={isLiveMode ? String(parentCategoryId ?? '') : parentCategory}
                onValueChange={(value) => {
                  if (isLiveMode) {
                    const numId = Number(value);
                    setParentCategoryId(numId);
                    const match = categoryList.find(c => c.id === numId);
                    if (match) setParentCategory(match.name as TransactionCategory);
                  } else {
                    setParentCategory(value as TransactionCategory);
                  }
                }}
              >
                <SelectTrigger id="parentCategory">
                  <SelectValue placeholder="Select parent category" />
                </SelectTrigger>
                <SelectContent>
                  {categoryList.map((category) => (
                    <SelectItem key={isLiveMode ? category.id : category.name} value={isLiveMode ? String(category.id) : category.name}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="subcategoryName">Subcategory Name</Label>
            <Input
              id="subcategoryName"
              value={subcategoryName}
              onChange={(e) => setSubcategoryName(e.target.value)}
              placeholder="e.g., Construction Materials"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Building materials, concrete, timber, bricks"
              rows={3}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="isActive">Active</Label>
            <Switch
              id="isActive"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!subcategoryName.trim() || (isLiveMode && categoryList.length === 0)}>
            {editingRule ? 'Update' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
